import type { ChannelKindEnumKey } from '../../../core/api/generated/models/ChannelKind';
import type { Conversation } from '../../../core/api/generated/models/Conversation';
import type { ConversationSession } from '../../../core/api/generated/models/ConversationSession';
import type { CrmContact } from '../../../core/api/generated/models/CrmContact';
import type { CrmContactChannelIdentity } from '../../../core/api/generated/models/CrmContactChannelIdentity';
import type { Message } from '../../../core/api/generated/models/Message';
import type { ConversationLabelChip, ConversationView } from '../models/conversation-view.types';

/**
 * The generated CrmContactChannelIdentity omits the `metadata` jsonb the API returns. The only
 * field the UI reads is `handle`: a provider-agnostic, display-ready identifier the enrichment
 * layer fills (e.g. an Instagram "@username"). The mapper renders it as-is — no per-provider
 * branching — so any future channel that enriches a handle works without UI changes.
 */
type ContactIdentityWithMetadata = CrmContactChannelIdentity & {
  metadata?: { handle?: string; name?: string } | null;
};

/**
 * The generated CrmContact type omits the `identities` array that the API actually returns
 * alongside the contact record. Until the generated type is regenerated, we extend it locally
 * so the mapper can access identities without casting to `any`.
 */
type ConversationContact = CrmContact & {
  identities?: readonly ContactIdentityWithMetadata[];
};

/**
 * The generated ConversationSession type omits the `teamName` field that the API enriches
 * via a LEFT JOIN onto `teams`. We extend it locally so the mapper can read it directly.
 */
type ConversationSessionWithTeam = ConversationSession & {
  teamName?: string | null;
};

export interface MapConversationInput {
  conversation: Conversation;
  contact: ConversationContact | null;
  sessions: readonly ConversationSessionWithTeam[];
  currentMembershipId: string | null;
  lastMessage?: Message | null;
  channelKind?: ChannelKindEnumKey | null;
  /** Labels assigned to this conversation; fetched by the store in bulk and injected here. */
  labels?: readonly ConversationLabelChip[];
}

export function mapConversationToView(input: MapConversationInput): ConversationView {
  const { conversation, contact, sessions, currentMembershipId, lastMessage, channelKind, labels } = input;

  const currentSession =
    sessions.find((session) => session.id === conversation.currentSessionId) ??
    sessions.find((session) => session.status === 'active') ??
    null;

  // Channel-scoped: the identity that actually opened THIS thread wins over the contact's
  // global primary — so a contact aggregating several identities still shows the right one
  // per conversation. Falls back to the primary-by-type logic when there's no counterparty.
  const counterpartyIdentity = pickContactIdentity(
    contact?.identities ?? [],
    channelKind,
    conversation.counterpartyExternalId,
  );
  const channelIdentity = counterpartyIdentity
    ? formatIdentityValue(counterpartyIdentity)
    : pickChannelIdentity(contact, channelKind, conversation.externalConversationId);
  const assignedToMe =
    currentSession?.assigneeType === 'membership' && currentSession.assigneeMembershipId === currentMembershipId;

  return {
    id: conversation.id,
    contactId: conversation.contactId,
    channelAccountId: conversation.channelAccountId,
    contact: formatContactName(contact, counterpartyIdentity) ?? `Contato ${shortId(conversation.contactId)}`,
    company: contact?.companyId ? `Empresa ${shortId(contact.companyId)}` : '',
    channel: channelKind ?? 'unknown',
    channelIdentity,
    // Prefer the JOIN-enriched team name from the API; fall back to a short-id placeholder
    // only when the server has a teamId but didn't include a name (shouldn't happen, but
    // guards against stale data during deploys).
    team: currentSession?.teamName
      ?? (currentSession?.teamId ? `Equipe ${shortId(currentSession.teamId)}` : 'Sem equipe'),
    teamId: currentSession?.teamId ?? null,
    owner: formatConversationOwner(currentSession, currentMembershipId),
    assignedToMe,
    status: conversation.status,
    priority: conversation.priority,
    lastMessage: previewMessage(lastMessage),
    lastMessageAt: formatRelativeTime(lastMessage?.sentAt ?? conversation.lastMessageAt ?? conversation.updatedAt),
    sla: conversation.status === 'snoozed' ? 'Pausado' : '--',
    labels: labels ?? [],
    // The API response carries unreadCount / manuallyUnread once the DB migration lands.
    // Safely default to 0 / false so old responses don't break the UI.
    unread: (conversation as Record<string, unknown>)['unreadCount'] as number ?? 0,
    manuallyUnread: Boolean((conversation as Record<string, unknown>)['manuallyUnread']),
    session: currentSession,
    createdAt: conversation.createdAt,
    firstResponseAt: conversation.firstResponseAt ?? null,
    awaitingReplySince: conversation.awaitingReplySince ?? null,
    awaitingTrack: conversation.awaitingTrack ?? null,
    snoozedUntil: conversation.snoozedUntil ?? null,
    replyWindow: extractReplyWindow(conversation),
    // Só false quando o backend afirma canal inativo; ausência = ativo (não bloqueia
    // por respostas antigas sem o campo).
    channelActive: (conversation as Record<string, unknown>)['channelActive'] !== false,
  };
}

function extractReplyWindow(conversation: Conversation): ConversationView['replyWindow'] {
  const raw = (conversation as Record<string, unknown>)['replyWindow'];
  if (!raw || typeof raw !== 'object') return null;
  const window = raw as { open?: unknown; expiresAt?: unknown; reopenWith?: unknown };
  if (typeof window.open !== 'boolean') return null;
  return {
    open: window.open,
    expiresAt: typeof window.expiresAt === 'string' ? window.expiresAt : null,
    reopenWith: window.reopenWith === 'template' ? 'template' : null,
  };
}

function formatContactName(
  contact: ConversationContact | null,
  counterpartyIdentity: ContactIdentityWithMetadata | null,
): string | null {
  const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim();
  if (name.length > 0) {
    return name;
  }
  // No real name: label by the identity that opened this thread, preferring an enriched
  // display name, then a handle (e.g. "@username"). Generic across channels — no provider branch.
  const metaName = counterpartyIdentity?.metadata?.name?.trim();
  if (metaName) {
    return metaName;
  }
  const handle = counterpartyIdentity?.metadata?.handle?.trim();
  return handle && handle.length > 0 ? handle : null;
}

function formatConversationOwner(session: ConversationSession | null, currentMembershipId: string | null): string {
  if (!session || session.assigneeType === 'unassigned') {
    return 'Fila';
  }

  if (session.assigneeType === 'agent') {
    return 'Agente IA';
  }

  if (session.assigneeMembershipId === currentMembershipId) {
    return 'Você';
  }

  return session.assigneeMembershipId ? `Membro ${shortId(session.assigneeMembershipId)}` : 'Membro';
}

export function previewMessage(message: Message | null | undefined): string {
  if (!message) {
    return '';
  }
  const text = message.contentText?.trim();
  if (text) {
    return text;
  }
  const attachment = message.attachments?.[0];
  if (attachment) {
    return attachmentLabel(attachment.contentType);
  }
  return '';
}

function attachmentLabel(contentType: string | null | undefined): string {
  const mime = (contentType ?? '').toLowerCase();
  if (mime.startsWith('image/')) {
    return '📷 Imagem';
  }
  if (mime.startsWith('video/')) {
    return '🎬 Vídeo';
  }
  if (mime.startsWith('audio/')) {
    return '🎤 Áudio';
  }
  return '📎 Documento';
}

/**
 * Pick the user-facing identifier for the conversation, preferring real contact identities
 * (phone/whatsapp/email/instagram) over the provider's internal `externalConversationId`,
 * which for WhatsApp Cloud is a thread id like "BR.1707493074031770" — not a phone number.
 *
 * Resolution order per channel kind:
 *   1. A matching identity on the contact (e.g. WhatsApp → phone or whatsapp identity).
 *   2. `externalConversationId` IF it doesn't look like an opaque provider thread id.
 *   3. A localized "Sem ..." fallback so the empty state stays readable.
 */
function pickChannelIdentity(
  contact: ConversationContact | null,
  kind: ChannelKindEnumKey | null | undefined,
  externalConversationId: string | null | undefined,
): string {
  const identity = pickContactIdentity(contact?.identities ?? [], kind);
  if (identity) {
    return formatIdentityValue(identity);
  }
  const external = externalConversationId?.trim();
  if (external && !looksLikeProviderThreadId(external)) {
    return external;
  }
  return identityFallback(kind);
}

function pickContactIdentity(
  identities: readonly ContactIdentityWithMetadata[],
  kind: ChannelKindEnumKey | null | undefined,
  counterpartyExternalId?: string | null,
): ContactIdentityWithMetadata | null {
  if (identities.length === 0) {
    return null;
  }
  // Channel-scoped first: the identity whose value is exactly the counterparty that opened
  // this conversation. Only then fall back to the primary-by-type heuristic below.
  if (counterpartyExternalId) {
    const exact = identities.find((identity) => identity.value === counterpartyExternalId);
    if (exact) return exact;
  }
  const preferredTypes = preferredIdentityTypes(kind);
  // First pass: primary identity matching a preferred type. Second pass: any matching.
  for (const type of preferredTypes) {
    const primary = identities.find((i) => i.type === type && i.isPrimary);
    if (primary) return primary;
  }
  for (const type of preferredTypes) {
    const any = identities.find((i) => i.type === type);
    if (any) return any;
  }
  return null;
}

function preferredIdentityTypes(kind: ChannelKindEnumKey | null | undefined): readonly string[] {
  switch (kind) {
    case 'whatsapp':
      return ['phone', 'whatsapp'];
    case 'sms':
    case 'voip':
      return ['phone'];
    case 'email':
      return ['email'];
    case 'instagram':
      return ['instagram'];
    default:
      return ['phone', 'whatsapp', 'email', 'instagram'];
  }
}

function formatIdentityValue(identity: ContactIdentityWithMetadata): string {
  // A provider-enriched display handle (e.g. "@username") always wins over the raw `value`
  // (which may be an opaque id like an IGSID). Generic across channels.
  const handle = identity.metadata?.handle?.trim();
  if (handle) {
    return handle;
  }
  if (identity.type === 'phone' || identity.type === 'whatsapp') {
    return formatBrazilianPhone(identity.value);
  }
  return identity.value;
}

/**
 * Brazilian phone formatter for the common case (E.164 starting with 55). Falls back to
 * the raw value if the input doesn't match the expected shape so other countries stay
 * readable instead of being mangled.
 */
function formatBrazilianPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    const country = digits.slice(0, 2);
    const ddd = digits.slice(2, 4);
    const rest = digits.slice(4);
    const half = rest.length - 4;
    return `+${country} ${ddd} ${rest.slice(0, half)}-${rest.slice(half)}`;
  }
  if (digits.length > 0 && /^\d+$/.test(digits)) {
    return `+${digits}`;
  }
  return raw;
}

/** Detects opaque provider thread ids that should NOT be shown to the user. */
function looksLikeProviderThreadId(value: string): boolean {
  // WhatsApp Cloud thread ids look like "BR.1707493074031770" or contain letters.
  return /[A-Za-z]/.test(value);
}

/**
 * Identifier fallback per channel kind, when neither the contact identity nor the
 * conversation external id are usable.
 */
function identityFallback(kind: ChannelKindEnumKey | null | undefined): string {
  switch (kind) {
    case 'whatsapp':
    case 'sms':
    case 'voip':
      return 'Sem número';
    case 'instagram':
      return 'Sem usuário';
    case 'email':
      return 'Sem e-mail';
    case 'webchat':
    case 'marketplace':
      return 'Sem identificador';
    default:
      return 'Sem número';
  }
}

export function formatRelativeTime(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  const now = new Date();
  const diffMinutes = Math.max(0, Math.round((now.getTime() - date.getTime()) / 60000));

  if (diffMinutes < 1) {
    return 'agora';
  }

  if (diffMinutes < 60) {
    return `${diffMinutes} min`;
  }

  if (diffMinutes < 24 * 60) {
    return `${Math.round(diffMinutes / 60)} h`;
  }

  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(date);
}

function shortId(value: string): string {
  return value.slice(0, 8);
}
